<div class="container mx-auto max-w-3xl px-4 md:px-6 pt-8 pb-4">
  <!-- Header -->
  <div class="flex items-center gap-3 mb-6">
    <button mat-icon-button (click)="cancel()">
      <mat-icon>arrow_back</mat-icon>
    </button>
    <div>
      <h2 class="text-2xl font-medium">Template verplaatsen</h2>
      @if (templateLabel()) {
        <p class="text-sm text-gray-500 mt-0.5">{{ templateLabel() }}</p>
      }
    </div>
  </div>

  <div class="bg-white rounded-lg p-6 shadow-sm">
    <mat-stepper [linear]="true" (selectionChange)="onStepChange($event)">

      <!-- Always show step numbers instead of edit/done icons -->
      <ng-template matStepperIcon="edit" let-index="index">
        {{ index + 1 }}
      </ng-template>
      <ng-template matStepperIcon="done" let-index="index">
        {{ index + 1 }}
      </ng-template>

      <!-- Step 1: Target unit selection -->
      <mat-step [completed]="!!selectedUnitId() && !!preview()">
        <ng-template matStepLabel>Doelunit</ng-template>

        <div class="py-4 space-y-4">
          <div class="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800 space-y-2">
            <p class="font-medium">Hoe werkt het verplaatsen?</p>
            <p>
              De template wordt verplaatst naar de nieuwe unit. Als er
              andere templates aan gekoppeld zijn, worden die automatisch
              meegenomen.
            </p>
            <p>
              Soms kan een template niet worden weggehaald uit de huidige
              unit, bijvoorbeeld omdat andere templates deze ook
              gebruiken. In dat geval wordt automatisch een kopie
              gemaakt. Als er al een identieke versie in de nieuwe unit
              staat, wordt die hergebruikt.
            </p>
            <p>
              Gebruikte media (afbeeldingen, audio) worden automatisch
              meegekopieerd.
            </p>
          </div>

          <mat-form-field class="w-full">
            <mat-label>Nieuwe unit</mat-label>
            <mat-select
              [ngModel]="selectedUnitId()"
              (ngModelChange)="selectedUnitId.set($event); onUnitSelected()"
              [disabled]="isLoadingPreview()"
            >
              @for (unit of availableUnits(); track unit._id) {
                <mat-option [value]="unit._id">{{ unit.name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>

          @if (isLoadingPreview()) {
            <p class="text-sm text-gray-400">Voorbeeld laden...</p>
          }
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button (click)="cancel()">Annuleren</button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="!selectedUnitId() || isLoadingPreview() || !preview()"
          >
            Volgende
          </button>
        </div>
      </mat-step>

      <!-- Step 2: Contact resolution -->
      <mat-step [completed]="maxStepReached() >= 1 && (!hasContacts() || isContactStepValid())">
        <ng-template matStepLabel>Contacten</ng-template>

        <div class="py-4">
          @if (!hasContacts()) {
            <div class="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
              <p>Er zijn geen contacten gekoppeld aan de template(s). Je kunt deze stap overslaan.</p>
            </div>
          } @else {
            <p class="text-sm text-gray-600 mb-4">
              De volgende contacten worden gebruikt in de template(s).
              Kies per contact of je deze wilt <strong>importeren</strong> (kopiëren naar de doelunit)
              of <strong>vervangen</strong> door een bestaand contact in de doelunit.
            </p>

            <div class="space-y-3">
              @for (row of contactRows(); track row._id; let i = $index) {
                <div class="border rounded-lg p-4 space-y-3">
                  <div class="flex items-center gap-2">
                    <mat-icon class="text-gray-400">person</mat-icon>
                    <div>
                      <p class="font-medium text-sm">{{ row.name }}</p>
                      @if (row.phoneNumber) {
                        <p class="text-xs text-gray-500">{{ row.phoneNumber }}</p>
                      }
                    </div>
                  </div>

                  <mat-form-field class="w-full">
                    <mat-label>Actie</mat-label>
                    <mat-select
                      [ngModel]="row.action"
                      (ngModelChange)="onContactActionChange(i, $event)"
                    >
                      <mat-option value="import">Importeren (kopiëren)</mat-option>
                      <mat-option value="replace">Vervangen</mat-option>
                    </mat-select>
                  </mat-form-field>

                  @if (row.action === 'replace') {
                    <mat-form-field class="w-full">
                      <mat-label>Vervangend contact</mat-label>
                      <mat-select
                        [ngModel]="row.targetContactId"
                        (ngModelChange)="onTargetContactChange(i, $event)"
                      >
                        @for (target of preview()?.targetUnitContacts; track target._id) {
                          <mat-option [value]="target._id">
                            {{ target.name }}
                            @if (target.phoneNumber) {
                              ({{ target.phoneNumber }})
                            }
                          </mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                </div>
              }
            </div>
          }
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button matStepperPrevious>Terug</button>
          <button
            mat-flat-button
            matStepperNext
            [disabled]="hasContacts() && !isContactStepValid()"
          >
            Volgende
          </button>
        </div>
      </mat-step>

      <!-- Step 3: Summary & Confirmation -->
      <mat-step>
        <ng-template matStepLabel>Bevestigen</ng-template>

        <div class="py-4 space-y-4">
          <h3 class="font-medium text-lg">Samenvatting</h3>

          <div class="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Template</span>
              <span class="font-medium">{{ templateLabel() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Van unit</span>
              <span class="font-medium">{{ sourceUnit()?.name }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Naar unit</span>
              <span class="font-medium">{{ selectedUnit()?.name }}</span>
            </div>
            @if (gotoChain().length > 1) {
              <div class="flex justify-between">
                <span class="text-gray-500">Gekoppelde templates</span>
                <span class="font-medium">{{ gotoChain().length }}</span>
              </div>
            }
            @if (mediaCopyCount() > 0) {
              <div class="flex justify-between">
                <span class="text-gray-500">Media wordt gekopieerd</span>
                <span class="font-medium">{{ mediaCopyCount() }}</span>
              </div>
            }
          </div>

          <!-- Reused templates info banners -->
          @for (entry of reusedTemplates(); track entry.correlationId) {
            <div class="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
              <mat-icon class="!text-sm !w-4 !h-4 !leading-4 align-middle mr-1">check_circle</mat-icon>
              {{ entry.autoResolvedInfo }}
            </div>
          }

          <!-- Template actions list (when chain has multiple entries) -->
          @if (gotoChain().length > 1) {
            <div>
              <h4 class="text-sm font-medium mb-2">Templates</h4>
              <div class="space-y-1">
                @for (entry of gotoChain(); track entry.correlationId) {
                  <div class="flex items-center gap-2 text-sm">
                    <mat-icon
                      class="!text-base !w-4 !h-4 !leading-4"
                      [class]="getActionClass(entry)"
                    >
                      {{ getActionIcon(entry) }}
                    </mat-icon>
                    <span>{{ entry.label }}</span>
                    <span class="text-gray-400">→</span>
                    <span [class]="getActionClass(entry)">
                      {{ getActionLabel(entry) }}
                    </span>
                  </div>
                }
              </div>
            </div>
          }

          <!-- Contact summary -->
          @if (hasContacts()) {
            <div>
              <h4 class="text-sm font-medium mb-2">Contacten</h4>
              <div class="space-y-1">
                @for (row of contactRows(); track row._id) {
                  <div class="flex items-center gap-2 text-sm">
                    <mat-icon class="text-gray-400 !text-base !w-4 !h-4 !leading-4">
                      {{ row.action === 'import' ? 'content_copy' : 'swap_horiz' }}
                    </mat-icon>
                    <span>{{ row.name }}</span>
                    <span class="text-gray-400">→</span>
                    @if (row.action === 'import') {
                      <span class="text-blue-600">Importeren</span>
                    } @else {
                      <span class="text-purple-600">
                        Vervangen door {{ getTargetContactName(row.targetContactId) }}
                      </span>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <div class="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800">
            <p>
              <strong>Let op:</strong>
              Na bevestiging wordt de template verplaatst naar de doelunit.
              Dit kan enkele seconden duren.
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <button mat-button matStepperPrevious>Terug</button>
          <button
            mat-flat-button
            color="primary"
            [disabled]="isSubmitting()"
            (click)="submit()"
          >
            @if (isSubmitting()) {
              Bezig met verplaatsen...
            } @else {
              Verplaatsen bevestigen
            }
          </button>
        </div>
      </mat-step>
    </mat-stepper>
  </div>
</div>
